import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { MeContext, useLogout, useMe, useSession } from '../lib/auth'
import { setReadOnly } from '../lib/api'
import { ErrorState } from './ErrorState'
import { AuthShell } from './AuthShell'
import { Button } from './ui'

/**
 * Renders children only with a session — and, unless `allowNoOrg`, an active
 * org. Everything below reads the session via useMe()/useActiveOrg(), never the
 * query directly (see MeContext).
 */
export function RequireAuth({ children, allowNoOrg = false }: { children: ReactNode; allowNoOrg?: boolean }) {
  const session = useSession()
  const loc = useLocation()

  // Keep the api client's write backstop in step with the session.
  setReadOnly(!!session.data?.impersonating)

  if (session.isPending) {
    return <div className="grid min-h-screen place-items-center text-sm text-slate-500">Loading…</div>
  }

  // Only when we never got a session: a failed background refetch keeps the last good one.
  if (session.isError && session.data === undefined) {
    // /me failed for a reason other than 401 — the api is down, not the user signed out.
    return (
      <div className="grid min-h-screen place-items-center p-6">
        <ErrorState message="Couldn't reach ShipDesk." onRetry={() => void session.refetch()} />
      </div>
    )
  }

  if (session.data === null) {
    const next = loc.pathname + loc.search
    return <Navigate to={next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`} replace />
  }

  if (!session.data.activeOrg && !allowNoOrg) {
    // A platform admin usually belongs to no org; their home is the console.
    if (session.data.isSuperAdmin) return <Navigate to="/admin/orgs" replace />
    return (
      <MeContext.Provider value={session.data}>
        <NoOrg />
      </MeContext.Provider>
    )
  }

  return <MeContext.Provider value={session.data}>{children}</MeContext.Provider>
}

/** Below RequireAuth: the /admin area. Checks the platform flag, never org membership. */
export function RequireSuperAdmin({ children }: { children: ReactNode }) {
  const me = useMe()
  if (!me.isSuperAdmin) return <Navigate to="/" replace />
  return <>{children}</>
}

/** Signed in, but a member of nothing — e.g. removed from their only organisation. */
function NoOrg() {
  const logout = useLogout()
  return (
    <AuthShell title="You're not part of an organisation" footer="ShipDesk is invite-only.">
      <p className="text-sm text-slate-600">
        Ask an admin of your team&rsquo;s ShipDesk to invite you. Open the invitation link they share and you&rsquo;ll land
        straight in their dashboard.
      </p>
      <Button className="mt-5 w-full" onClick={() => logout.mutate()} disabled={logout.isPending}>
        Sign out
      </Button>
    </AuthShell>
  )
}
